/**
 * <Dinero> — el ÚNICO componente que formatea montos (skill diseno-visual-mg).
 * Regla de precios de la clienta (enmienda 2026-10-09): los $ del sistema son $ REALES; Bs = $ × tasa 2 (paralela);
 * el "$ a tasa BCV" es Bs ÷ tasa BCV y solo se muestra.
 * - dual   : "$ 1.234,56" arriba y "Bs 1.170.000,00" debajo (gris, pequeño).
 * - triple : Bs · $ a BCV · $ — para POS/cotización/documentos.
 * - divisas: $ en divisa y $ a BCV, con el MISMO peso y cada uno con su etiqueta y su color (pedido del cliente
 *             11-10-2026: el referencial a BCV debe verse igual que el real) — lista de precios del POS y cobro.
 * - bs     : solo bolívares (impresión).
 * - usd    : solo dólares.
 * Usa SIEMPRE las tasas del documento (`doc`); si no hay documento, las vigentes y marca "ref.". Cuando el documento
 * trae sus Bs exactos (redondeados por línea), pásalos en `bs` para que lo mostrado sea lo impreso.
 */
import { formatearBs, formatearUsd, Money, type MoneyInput } from '@mg/shared';
import { cn } from '@/lib/utils.ts';

export interface TasasDocumento {
  tasa_bcv: string;
  /** Tasa 2 / paralela: la que convierte $ reales a Bs. Si falta, se usa la BCV (documentos viejos). */
  tasa_paralela?: string;
  factor_divisa?: string;
}

export interface DineroProps {
  /** Monto en la moneda indicada por `moneda` (default USD = $ reales). */
  valor: MoneyInput;
  moneda?: 'USD' | 'BS';
  modo?: 'dual' | 'triple' | 'bs' | 'usd' | 'divisas';
  /** Tasas congeladas del documento. */
  doc?: TasasDocumento | undefined;
  /** Tasas vigentes cuando aún no hay documento (carrito): se muestra "ref.". */
  referencial?: TasasDocumento | undefined;
  /** Bs exactos del documento (ya redondeados por línea); si se dan, no se recalculan. */
  bs?: MoneyInput | undefined;
  /** Compatibilidad: antes elegía la tasa de los Bs. Hoy los Bs siempre salen de la tasa 2. */
  tasaBs?: 'bcv' | 'paralela' | undefined;
  className?: string | undefined;
  /** Alinear a la derecha (tablas). */
  alineado?: 'izquierda' | 'derecha' | undefined;
}

export function Dinero({ valor, moneda = 'USD', modo = 'dual', doc, referencial, bs: bsExacto, className, alineado = 'derecha' }: DineroProps) {
  const tasas = doc ?? referencial;
  const esRef = !doc && !!referencial;
  let monto: Money;
  try {
    monto = Money.parse(valor);
  } catch {
    return <span className={cn('text-mg-rojo', className)}>—</span>;
  }
  const paralela = tasas ? (tasas.tasa_paralela ?? tasas.tasa_bcv) : null;

  const usd = moneda === 'USD' ? monto : paralela ? monto.bsAUsd(paralela) : null;
  let bs: Money | null = moneda === 'BS' ? monto : paralela ? monto.usdABs(paralela).redondear2() : null;
  if (bsExacto !== undefined) {
    try {
      bs = Money.parse(bsExacto);
    } catch {
      /* se deja el calculado */
    }
  }
  const clase = cn('num inline-flex flex-col leading-tight', alineado === 'derecha' ? 'items-end text-right' : 'items-start text-left', className);
  const sufijoRef = esRef ? <span className="text-[10px] text-muted-foreground ml-1">ref.</span> : null;

  if (modo === 'usd') return <span className={clase}>{usd ? formatearUsd(usd) : '—'}</span>;
  if (modo === 'bs') return <span className={clase}>{bs ? formatearBs(bs) : '—'}</span>;

  if (modo === 'divisas') {
    const aBcv = bs && tasas ? bs.bsAUsd(tasas.tasa_bcv) : null;
    return (
      <span className={cn(clase, 'gap-0.5')}>
        <span className="font-semibold text-mg-verde-oscuro" data-moneda="divisa">{usd ? formatearUsd(usd) : '—'} <span className="text-[0.8em] font-medium uppercase tracking-wide opacity-80">divisa</span></span>
        <span className="font-semibold text-mg-azul" data-moneda="bcv">{aBcv ? formatearUsd(aBcv) : '—'} <span className="text-[0.8em] font-medium uppercase tracking-wide opacity-80">BCV</span></span>
      </span>
    );
  }

  if (modo === 'triple') {
    const usdBcv = bs && tasas ? bs.bsAUsd(tasas.tasa_bcv) : null;
    return (
      <span className={cn(clase, 'gap-0.5')}>
        <span className="font-semibold">{bs ? formatearBs(bs) : '—'}{sufijoRef}</span>
        <span className="text-[11px] text-muted-foreground">{usdBcv ? formatearUsd(usdBcv) : '—'} <span className="opacity-70">a BCV</span></span>
        <span className="text-[11px] text-muted-foreground">{usd ? formatearUsd(usd) : '—'} <span className="opacity-70">divisa</span></span>
      </span>
    );
  }

  return (
    <span className={clase}>
      <span className="font-medium">{usd ? formatearUsd(usd) : '—'}{sufijoRef}</span>
      <span className="text-[10.5px] text-muted-foreground">{bs ? formatearBs(bs) : '—'}</span>
    </span>
  );
}
